import { forwardRef, HTMLAttributes, useMemo } from "react";
import classNames from "classnames";

import "./TreeItem.css";
import GrowthCloudLink from "../../../Sidebar/Link/GrowthCloudLink";
import { useParams } from "react-router-dom";
import CollapseLink from "../../../Sidebar/CollapseLink";
import { TreeItem as ITreeItem } from "../../types";
import EmptyTreeItem from "./EmptyTreeItem";
import CustomLinkOptions, { CustomLinkOptionsProps } from "./CustomLinkOptions";
import GlobalAppOptions from "./GlobalAppOptions";
import CloudAppViewerOptions from "./CloudAppViewerOptions";
import CloudAppOwnerOptions from "./CloudAppOwnerOptions";

interface StartingProps extends Omit<HTMLAttributes<HTMLLIElement>, "id"> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  ghost?: boolean;
  draggableProps?: any;
  indicator?: boolean;
  indentationWidth: number;
  text: string;
  onCollapse?: (id?: string) => void;
  onRemove?: () => void;
  wrapperRef?(node: HTMLLIElement): void;
  item?: ITreeItem;
  isDragging?: boolean;
  setExistingPageTo: React.Dispatch<React.SetStateAction<any>>;
  setExistingCloudApp: React.Dispatch<React.SetStateAction<any>>;
  setForceUpdateDrawer: React.Dispatch<React.SetStateAction<any>>;
}

export type Props = StartingProps & CustomLinkOptionsProps;

export const TreeItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      childCount,
      clone,
      depth,
      disableSelection,
      disableInteraction,
      ghost,
      draggableProps,
      indentationWidth,
      indicator,
      collapsed,
      onCollapse,
      onRemove,
      style,
      text,
      wrapperRef,
      item,
      isDragging,
      setEditingItem,
      setAddPage,
      setSelectedPage,
      setCloudAppDrawerOpened,
      setCloudAppDrawerType,
      setCloudAppFolder,
      setExistingPageTo,
      setExistingCloudApp,
      setForceUpdateDrawer,
      ...props
    },
    ref
  ) => {
    const { tenantID } = useParams();
    const data = item?.data;

    if (!data) return null;

    const itemURL = useMemo(() => {
      let url = "Link" in data ? data.Link : "";

      if (data.Type === "ModuleLink") {
        url = `/clouds/${tenantID}/records/${data.ModuleAPIName}`;
      }
      if (data.Type === "PageLink") {
        url = `/clouds/${tenantID}/workspaces/${data.WorkspaceID}/pages/${data.PageID}/views/None`;
      }

      return url;
    }, [data, tenantID]);

    const isEmpty = ["Empty", "EmptySidebar"].includes(data.Type);

    return (
      <li
        className={classNames(
          "Wrapper tw:my-1",
          clone && "clone",
          ghost && "ghost",
          indicator && "indicator",
          disableSelection && "disableSelection",
          disableInteraction && "disableInteraction"
        )}
        ref={wrapperRef}
        style={
          {
            "--spacing": `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
        {...props}
      >
        <div
          className="TreeItem"
          ref={ref}
          style={style}
          {...(isEmpty ? {} : draggableProps)}
        >
          {isEmpty && (
            <EmptyTreeItem
              data={data}
              open={true}
              draggableProps={draggableProps}
            />
          )}
          {["ModuleLink", "Direct", "PageLink", "External"].includes(
            data.Type
          ) && (
            <GrowthCloudLink
              title={data.Title}
              link={itemURL}
              type={data.Type}
              linkId={data.id}
              icon={
                data.Type === "Direct"
                  ? "mdi mdi-database-arrow-right-outline"
                  : "Icon" in data
                  ? typeof data.Icon !== "string"
                    ? data?.Icon?.PublicURL
                    : typeof data.Icon === "string"
                    ? data.Icon
                    : ""
                  : ""
              }
              iconURL={"IconURL" in data ? data.IconURL : ""}
              options={
                <CustomLinkOptions
                  item={item}
                  setEditingItem={setEditingItem}
                  setAddPage={setAddPage}
                  setSelectedPage={setSelectedPage}
                  setCloudAppDrawerOpened={setCloudAppDrawerOpened}
                  setCloudAppDrawerType={setCloudAppDrawerType}
                  setCloudAppFolder={setCloudAppFolder}
                />
              }
              isDragging={isDragging}
            />
          )}
          {data.Type === "Group" && (
            <CollapseLink
              item={item}
              onExpand={onCollapse}
              onCollapse={onCollapse}
              options={
                <CustomLinkOptions
                  item={item}
                  setEditingItem={setEditingItem}
                  setAddPage={setAddPage}
                  setSelectedPage={setSelectedPage}
                  setCloudAppDrawerOpened={setCloudAppDrawerOpened}
                  setCloudAppDrawerType={setCloudAppDrawerType}
                  setCloudAppFolder={setCloudAppFolder}
                />
              }
              isDragging={!!isDragging}
            />
          )}
          {data.Type === "CloudAppViewer" && (
            <CollapseLink
              item={item}
              onExpand={onCollapse}
              onCollapse={onCollapse}
              options={
                !!data.GlobalAppID ? (
                  <GlobalAppOptions globalAppID={data.GlobalAppID} />
                ) : (
                  <CloudAppViewerOptions id={item.id} />
                )
              }
              isDragging={!!isDragging}
              isApp
            />
          )}
          {data.Type === "CloudAppOwner" && (
            <CollapseLink
              item={item}
              onExpand={onCollapse}
              onCollapse={onCollapse}
              options={
                <CloudAppOwnerOptions
                  item={item}
                  setAddPage={setAddPage}
                  setExistingPageTo={setExistingPageTo}
                  setCloudAppDrawerOpened={setCloudAppDrawerOpened}
                  setCloudAppDrawerType={setCloudAppDrawerType}
                  setExistingCloudApp={setExistingCloudApp}
                  setForceUpdateDrawer={setForceUpdateDrawer}
                />
              }
              isDragging={!!isDragging}
              isApp
            />
          )}
        </div>
      </li>
    );
  }
);
